/* eslint-disable react-hooks/exhaustive-deps */

import { QrcodeOutlined, SaveOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Steps } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as SERVICE from '../../constants/service.types';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InputTag from '../../components/InputTag';
import LocationSearchLocationIQ from '../../components/LocationSearchLocationIQ';
import regex from '../../constants/system.keys';
import { addExternalDeviceReturn, checkExternalDeviceReturn, socketEmitEvent, } from '../../redux/actions';

const { Step } = Steps;
const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};

const GPS_TYPE = 'gps_location';

export const AddGpsComponent = ({
  gatewaySerial,
  onUpdate,
  handleAddSuccessful,
  checkDeviceReturn,
  activeDeviceReturn,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [, forceUpdate] = useState();
  const [requestId, setRequestId] = useState(new Date().getTime());
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [serialInput, setSerialInput] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [isBlock, setIsBlock] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [address, setAddress] = useState({
    note: '',
  });
  const [alertUpdate, setAlertUpdate] = useState({
    visible: false,
    type: null,
    message: null,
  });

  const { namespace, addExternalDevice, updateDevice, checkExternalDevice } =
    useSelector((state) => state);

  const [updateReturn, setUpdateReturn] = useState();
  const serialRef = useRef(null);

  useEffect(() => {
    forceUpdate({});

    return () => {
      setIsLoading(false);
      setStep(0);
      setSerialInput('');
      setIsBlock(false);
      setIsLoad(false);
      setAddress({
        note: '',
      });
      setAlertUpdate({
        visible: false,
        type: null,
        message: null,
      });
      setUpdateReturn(null);
    };
  }, []);

  useEffect(() => {
    setIsLoading(false);
    if (
      checkDeviceReturn &&
      (checkDeviceReturn.result === -2 || checkDeviceReturn.result === -3)
    ) {
      form.setFields([
        {
          name: 'serial',
          errors: [checkDeviceReturn.message],
        },
      ]);
    }
    if (checkDeviceReturn && checkDeviceReturn.result === 1) {
      if (
        checkDeviceReturn.data &&
        JSON.parse(checkDeviceReturn.data)?.serial
      ) {
        setDeviceSerial(JSON.parse(checkDeviceReturn.data)?.serial);
        dispatch(checkExternalDeviceReturn(null));
        form.resetFields();
        return setStep(1);
      } else {
        form.setFields([
          {
            name: 'serial',
            errors: ['Serial Invalid'],
          },
        ]);
      }
    }
  }, [activeDeviceReturn, checkDeviceReturn, dispatch, form, serialInput]);

  const onCheckExternalDevice = (serial) => {
    let data = {
      namespace,
      deviceType: GPS_TYPE,
      extraInfo: {
        factorySerial: serial,
      },
    };

    let dataObj = {
      appServiceType: SERVICE.CHECK_EXTERNAL_DEVICE,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };
    return dispatch(socketEmitEvent(dataObj));
  };

  const onAddRemote = ({ serial, deviceSerial, name }) => {
    const data = {
      namespace,
      deviceType: GPS_TYPE,
      serial: deviceSerial,
      extraInfo: {
        name: name,
        gatewaySerial: gatewaySerial,
        factorySerial: serial,
      },
    };

    let dataObj = {
      appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };
    return dispatch(socketEmitEvent(dataObj));
  };

  const onFinish = useCallback(
    (values) => {
      setIsLoading(true);
      if (step === 0) {
        setSerialInput(values.serial);
        return onCheckExternalDevice(values.serial);
      }
      if (step === 1) {
        setNameInput(values.name);
        return onAddRemote({
          serial: serialInput,
          deviceSerial,
          name: values.name,
        });
      }
    },
    [onAddRemote, onCheckExternalDevice, serialInput, step, deviceSerial]
  );

  /////////////////////////////////////////////////////////////

  useEffect(() => {
    if (updateDevice && updateDevice.requestId === requestId) {
      setUpdateReturn(updateDevice);
    }
  }, [updateDevice]);

  useEffect(() => {
    form.setFieldsValue({ serial: serialInput });
    return () => {
      setAlertUpdate({ visible: false });
      form.resetFields();
    };
  }, [form, serialInput]);

  useEffect(() => {
    if (serialRef.current && step === 0) {
      setTimeout(() => {
        serialRef.current.input.focus();
      }, 1);
    }
  }, [serialRef, step]);

  useEffect(() => {
    if (addExternalDevice) {
      setIsLoading(false);
      dispatch(addExternalDeviceReturn(null));
      form.setFieldsValue({
        nameDefault: nameInput,
        lastSerial: serialInput,
      });
      return setStep(2);
    }
  }, [addExternalDevice]);

  const onChangeStep = (step) => {
    form.resetFields();
    setStep((state) => (state > step ? step : state));
  };

  const handleAddressChange = (address) => {
    form.setFieldsValue({
      location: address,
    });
    setAddress((state) => ({
      ...state,
      note: address,
    }));
  };

  const handleSelect = (address) => {
    form.setFieldsValue({
      location: address?.note ?? '',
    });
    setAddress(address);
  };

  useEffect(() => {
    if (updateReturn && updateReturn.serial === deviceSerial) {
      setIsLoad(false);
      if (updateReturn.isSuccess) {
        setUpdateReturn(null);
        setStep(0);
        form.resetFields();
        handleAddSuccessful();
      } else {
        setAlertUpdate({
          visible: true,
          type: 'warning',
          message: t('Something went wrong, try again!!'),
        });
      }
    }
  }, [form, handleAddSuccessful, deviceSerial, t, updateReturn]);

  const handleOnUpdate = useCallback(
    (values) => {
      if (address && requestId) {
        console.log('Address: ', address);
        const data = {
          name: values.nameDefault,
          note: values.location ?? '',
          serial: deviceSerial,
          lat: address.lat ?? 0,
          lng: address.lng ?? 0,
          tags: values.tags?.join(';')?.toLowerCase() || ';',
        };
        console.log('GPS Data: ', data);

        const dataTimeRequest = new Date().getTime();
        setRequestId(dataTimeRequest);
        setIsLoad(true);
        return onUpdate(data, dataTimeRequest);
      }
    },
    [address, onUpdate, requestId, deviceSerial]
  );

  if (step === 2) {
    return (
      <div className="edit-gateway-component">
        <div className="steps-add-gateways">
          <Steps
            size="small"
            current={step}
            onChange={onChangeStep}
            type="navigation"
            className="site-navigation-steps"
          >
            <Step title={t('Scan')} />
            <Step title={t('Create')} />
            <Step title={t('Update')} />
          </Steps>
        </div>
        <Form
          form={form}
          {...layout}
          name="form_edit_gateway"
          onFinish={(values) => handleOnUpdate(values)}
        >
          <Form.Item
            name="nameDefault"
            label={t('Name')}
            rules={[
              {
                required: true,
                message: t('Please input the name of GPS!'),
              },
              {
                pattern: regex.isName,
                message: t('Please enter only letters and numbers!'),
              },
              {
                max: 80,
                message: t('Please input up to 80 characters!'),
              },
            ]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item name="lastSerial" label={t('Factory serial')}>
            <Input disabled={true} />
          </Form.Item>

          <Form.Item
            name="location"
            rules={[
              {
                pattern: regex.isName,
                message: t('Please enter only letters and numbers!'),
              },
              {
                max: 200,
                message: t('Please input up to 200 characters!'),
              },
            ]}
            label={t('Location')}
          >
            <LocationSearchLocationIQ
              address={address.note}
              onChange={handleAddressChange}
              onAddressSelect={handleSelect}
              setIsBlock={setIsBlock}
            />
          </Form.Item>
          <Form.Item
            name="tags"
            className="tags-device"
            label={t('Tag')}
            style={{ borer: '1px solid #434343', borderRadius: '2px' }}
          >
            <InputTag numberTag={3} nameAdd="Add tag" />
          </Form.Item>
          <div className="edit-gateway-alert">
            {alertUpdate.visible && (
              <Alert
                {...alertUpdate}
                showIcon
                closable
                onClose={() => setAlertUpdate({ visible: false })}
              />
            )}
          </div>
          <div className="btn-update-on-add-gateway">
            <Button onClick={() => handleAddSuccessful()}>{t('Skip')}</Button>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  type="primary"
                  loading={isLoad}
                  disabled={
                    form.getFieldsError().filter(({ errors }) => errors.length)
                      .length
                  }
                >
                  {t('Update')}
                </Button>
              )}
            </Form.Item>
          </div>
        </Form>
      </div>
    );
  }
  return (
    <div className="add-box-component">
      <div className="steps-add-gateways">
        <Steps
          size="small"
          current={step}
          onChange={onChangeStep}
          type="navigation"
          className="site-navigation-steps"
        >
          <Step title={t('Scan')} />
          <Step title={t('Create')} />
          <Step title={t('Update')} />
        </Steps>
      </div>
      {step === 0 && (
        <div className="title-add-box">
          {t('Enter your device serial code, please find it behind device')}
        </div>
      )}
      <div className="main-form-add-box">
        <Form
          form={form}
          {...(step === 0 ? {} : layout)}
          name="horizontal_login"
          layout={step === 0 ? 'inline' : 'horizontal'}
          onFinish={onFinish}
        >
          {step === 0 ? (
            <Form.Item
              name="serial"
              rules={[
                {
                  required: true,
                  message: t('Please enter the device serial!'),
                },
                {
                  pattern: regex.isName,
                  message: t('Please enter only letters and numbers!'),
                },
                {
                  max: 80,
                  message: t('Please input up to 80 characters!'),
                },
              ]}
            >
              <Input
                ref={serialRef}
                prefix={<QrcodeOutlined className="site-form-item-icon" />}
                placeholder={t('Factory serial')}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="defaultSerial" label={t('Factory serial')}>
                <Input
                  defaultValue={serialInput}
                  disabled
                  prefix={<QrcodeOutlined className="site-form-item-icon" />}
                />
              </Form.Item>
              <Form.Item
                name="name"
                label={t('Name')}
                rules={[
                  {
                    required: true,
                    message: t('Please input the name of GPS!'),
                  },
                  {
                    pattern: regex.isName,
                    message: t('Please enter only letters and numbers!'),
                  },
                  {
                    max: 80,
                    message: t('Please input up to 80 characters!'),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </>
          )}
          <div className="btn-update-on-add-gateway">
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ marginLeft: '0px' }}
                  loading={isLoading}
                >
                  {step === 0 ? t('Validate') : t('Create')}
                </Button>
              )}
            </Form.Item>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddGpsComponent;
